import {
    Injectable, inject
} from "@angular/core";

import {
    MatDialog,
    MatDialogRef
} from "@angular/material/dialog";

import { SupportRequestComponent } from "../../dialogs/support-request/support-request.component";

/**
 * Dialog Service that opens the {@link SupportRequestComponent} dialog.
 *
 * This service is responsible solely for launching the support request dialog. It is a focused,
 * single-responsibility service extracted from the broader `SupportService` to separate the
 * dialog-opening concern from API and notification concerns.
 *
 * @providedIn root — available as a root singleton throughout the application; no explicit providers array entry is required.
 *
 * @example
 * ```typescript
 * // Inject and open the support dialog
 * private readonly _supportRequestDialogService = inject(SupportRequestDialogService);
 *
 * openSupportDialog(): void {
 *     this._supportRequestDialogService.open();
 * }
 * ```
 *
 * @example
 * ```typescript
 * // React to dialog close
 * private readonly _supportRequestDialogService = inject(SupportRequestDialogService);
 *
 * openSupportDialog(): void {
 *     const dialogRef = this._supportRequestDialogService.open();
 *     dialogRef.afterClosed().subscribe((result) => {
 *         // handle result
 *     });
 * }
 * ```
 */
@Injectable({
    providedIn: "root"
})
export class SupportRequestDialogService {
    private readonly _dialog = inject(MatDialog);

    /**
     * Opens the {@link SupportRequestComponent} dialog with a fixed 600px width and close-prevention.
     *
     * @returns The {@link MatDialogRef} for the opened `SupportRequestComponent` dialog.
     */
    open(): MatDialogRef<SupportRequestComponent> {
        return this._dialog.open(SupportRequestComponent, {
            data: {},
            width: "600px",
            maxHeight: "100%",
            disableClose: true
        });
    }
}
